import {
    RENDER_READINESS_CHANNELS,
    type RenderReadinessBridge,
    type RenderReadinessChannel,
    type RenderReadinessRevisions,
    type RenderShadowDiagnostics,
} from './readiness'

const createRevisionState = (): RenderReadinessRevisions => (
    Object.fromEntries(
        RENDER_READINESS_CHANNELS.map(channel => [channel, 0]),
    ) as unknown as RenderReadinessRevisions
)

/**
 * The only ModelViewer module that publishes synchronization state on window.
 * Acknowledgements for older work are ignored once a newer revision is
 * expected, preventing a late WebGL frame from releasing Puppeteer's barrier.
 */
class BrowserRenderReadinessBridge implements RenderReadinessBridge {
    private readonly expectedRevisions = createRevisionState()
    private readonly revisions = createRevisionState()
    private error?: string
    private modelReady = false
    private requestListenerReady = false
    private shadow: RenderShadowDiagnostics = {cacheSize: 0}

    public reset(
        channel: RenderReadinessChannel,
        revision: number,
    ) {
        this.expectedRevisions[channel] = revision
        this.revisions[channel] = 0
    }

    public acknowledge(
        channel: RenderReadinessChannel,
        revision: number,
    ) {
        const expectedRevision = this.expectedRevisions[channel]
        if (
            expectedRevision !== 0
            && expectedRevision !== revision
        ) return false

        this.expectedRevisions[channel] = revision
        this.revisions[channel] = revision
        return true
    }

    public fail(message: string) {
        this.error = message
    }

    public clearFailure() {
        this.error = undefined
    }

    public setModelReady(ready: boolean) {
        this.modelReady = ready
    }

    public setRequestListenerReady(ready: boolean) {
        this.requestListenerReady = ready
    }

    public setShadowDiagnostics(
        diagnostics: Partial<RenderShadowDiagnostics>,
    ) {
        this.shadow = {...this.shadow, ...diagnostics}
    }

    public snapshot() {
        return {
            error: this.error,
            expectedRevisions: {...this.expectedRevisions},
            modelReady: this.modelReady,
            requestListenerReady: this.requestListenerReady,
            revisions: {...this.revisions},
            shadow: {...this.shadow},
        }
    }
}

export const renderReadiness: RenderReadinessBridge = (
    new BrowserRenderReadinessBridge()
)

if (typeof window !== 'undefined') {
    window.modelViewerReadiness = renderReadiness
}
